import { SSRMeasures } from '@confluence/action-measures';
import type {
	PreloadFailureReason,
	PreloadFailureResult,
} from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types';
import { MacrosQuery, getMultiMacroQueryBlocklistFF } from '@confluence/fabric-extension-queries';
import { bestEffortTimeouts, query } from '@confluence/query-preloader-tools';

import { DEFAULT_LOAD_TIMEOUT, DEFAULT_PRELOAD_COUNT } from './preloader-constants';

const bestEffortWithTimeout = async (
	name: string,
	task: () => Promise<any>,
	timeout: number = DEFAULT_LOAD_TIMEOUT,
	timeoutList: { [key: string]: boolean },
): Promise<boolean> => {
	const timeoutPromise = new Promise<boolean>((resolve) =>
		setTimeout(() => {
			resolve(false);
		}, timeout),
	);

	timeoutList[name] = true;
	const runTask = async () => {
		await SSRMeasures.run(`ssr-app/prepare/preloadQuery/bestEffort:${name}`, task);
		timeoutList[name] = false;
		return true;
	};

	return Promise.race([timeoutPromise, runTask()]);
};

const failureIfNotSuccess = (reason: PreloadFailureReason) => (success: boolean) =>
	success ? undefined : { MacrosQuery_extensions: reason };

const preloadMacrosSSRAndReturnTopXFailed = async (
	contentId: string,
	featureFlags: { [key: string]: string | number | boolean },
) => {
	// Best effort timeout for macro loading tasks
	const macroLoaderTimeout = DEFAULT_LOAD_TIMEOUT;

	const blocklist = getMultiMacroQueryBlocklistFF(featureFlags);
	const preloadResult = await bestEffortWithTimeout(
		'MacrosQuery_preload',
		() => {
			return query({
				variables: {
					first: DEFAULT_PRELOAD_COUNT,
					contentId,
					blocklist,
				},
				query: MacrosQuery,
				context: { single: true },
			});
		},
		macroLoaderTimeout,
		bestEffortTimeouts,
	).then(failureIfNotSuccess('timeout-rendered-macros'));

	return Object.assign({}, ...[preloadResult]);
};

export async function preloadMacrosSSRMultiple(
	contentId: string,
	featureFlags: { [key: string]: string | number | boolean },
): Promise<PreloadFailureResult | undefined> {
	// Function has early return so we extracted it out to avoid setting global in every return
	return (window['__SSR_MACRO_PRELOAD_TOPX_FAILS__'] = await preloadMacrosSSRAndReturnTopXFailed(
		contentId,
		featureFlags,
	));
}
